/**
 * plan-expiry.ts — Quy tắc dùng chung cho hộp thoại Platform (tạo tổ chức, đổi gói, gia hạn tay):
 *  - Q4: gói TRẢ PHÍ (giá tháng > 0, không phải Miễn phí / Doanh nghiệp) bắt buộc nhập ngày hết hạn (tương lai).
 *  - Q1: lỗi từ máy chủ gắn vào đúng ô (mã giới thiệu sai → ô mã; thiếu hạn → ô ngày) thay vì chỉ hiện toast.
 *  - Q6: mỗi lần mở hộp thoại gia hạn sinh một khoá idempotency — bấm trùng / gửi lại không tạo đơn thứ hai.
 */
import type { AxiosError } from 'axios'

export interface PlanPriceLite {
  id: string
  monthlyPrice: number | null
}

/** Q4: gói cần ngày hết hạn — khớp backend `planRequiresExpiry`. */
export function planRequiresExpiry(plan: PlanPriceLite | null | undefined): boolean {
  return !!plan && plan.id !== 'free' && typeof plan.monthlyPrice === 'number' && plan.monthlyPrice > 0
}

/** 'YYYY-MM-DD' (ô chọn ngày) → hết hạn vào CUỐI ngày theo giờ Việt Nam (ISO). Rỗng / sai → null. */
export function endOfDayVn(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null
  const d = new Date(`${date}T23:59:59+07:00`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** Lỗi ô ngày hết hạn (null = hợp lệ). `required` = gói trả phí. */
export function expiryDateError(date: string, required: boolean, now: Date = new Date()): string | null {
  if (!date) return required ? 'Gói trả phí bắt buộc nhập ngày hết hạn' : null
  const iso = endOfDayVn(date)
  if (!iso) return 'Ngày hết hạn không hợp lệ'
  if (required && new Date(iso).getTime() <= now.getTime()) return 'Ngày hết hạn của gói trả phí phải từ hôm nay trở đi'
  return null
}

/** Mã lỗi `code` trong phản hồi backend ({ error, code }). */
export function apiErrorCode(err: unknown): string | undefined {
  return (err as AxiosError<{ code?: string }>)?.response?.data?.code
}

export type OrgFormField = 'referralCode' | 'expiresAt'

/** Q1: lỗi máy chủ thuộc ô nào của form (để báo ngay tại ô, giữ nguyên dữ liệu đã nhập). */
export function fieldForApiError(code: string | undefined): OrgFormField | null {
  if (!code) return null
  if (code === 'CODE_INVALID' || code.startsWith('REFERRAL') || code === 'SELF_REFERRAL') return 'referralCode'
  if (code === 'EXPIRY_REQUIRED' || code === 'EXPIRY_INVALID') return 'expiresAt'
  return null
}

/** Q6: khoá idempotency mới (UUID) — sinh mỗi lần mở hộp thoại gia hạn. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0')
  return `${hex()}${hex()}-${hex()}-4${hex().slice(1)}-a${hex().slice(1)}-${hex()}${hex()}${hex()}`
}
